import { FC, useEffect, useState } from "react";
import { getSeasons, getTeamGradeIds } from "../../cricket-api/combined-api";
import { Grade, Season, Team } from "../../schema/schema";
import { MultiSelect, Select } from "@mantine/core";
import uniqWith from "lodash.uniqwith";
import { getScorecards } from "../../cricket-api/season-api";
import { ComposedChart, Legend, Line, Tooltip, XAxis, YAxis, ResponsiveContainer } from "recharts";
import uniq from "lodash.uniq";

const palette = [
	"#30c5ff",
	"#22c55e",
	"#f59e0b",
	"#fb7185",
	"#a78bfa",
	"#14b8a6",
	"#ef4444",
	"#94a3b8"
];

const getColorForName = (name: string) => {
	const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
	return palette[hash % palette.length];
};

const getRandomSelection = (players: string[], count: number) => {
	if (players.length <= count) {
		return players;
	}

	const shuffled = [...players].sort(() => Math.random() - 0.5);
	return shuffled.slice(0, count);
};

const SeasonInsights: FC<{ orgId: string }> = ({ orgId }) => {
	const [teams, setTeams] = useState<Team[]>([]);
	const [seasons, setSeasons] = useState<Season[]>([]);
	const [selectedSeason, setSelectedSeason] = useState<Season | null>(null);
	const [grades, setGrades] = useState<Grade[]>([]);
	const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null);
	const [battersList, setBattersList] = useState<string[]>([]);
	const [selectedBatters, setSelectedBatters] = useState<string[]>([]);
	const [bowlersList, setBowlersList] = useState<string[]>([]);
	const [selectedBowlers, setSelectedBowlers] = useState<string[]>([]);

	const [battingChartData, setBattingChartData] = useState<
		Record<string, string | number | null>[]
	>([]);
	const [bowlingChartData, setBowlingChartData] = useState<
		Record<string, string | number | null>[]
	>([]);

	useEffect(() => {
		(async () => {
			const seasons = await getSeasons(orgId);
			setSeasons(seasons);
			if (!selectedSeason) {
				setSelectedSeason(seasons[0]);
			}
		})();
	}, [orgId, selectedSeason]);

	useEffect(() => {
		(async () => {
			const teams: Team[] = await getTeamGradeIds(orgId, selectedSeason?.Id || "");

			const seasonGrades = teams.reduce(
				(acc: Grade[], team) => [...acc, ...team.Grades.filter(g => g.IsCurrent)],
				[]
			);

			setTeams(teams);
			setGrades(uniqWith(seasonGrades, (s1, s2) => s1.Name === s2.Name));
			if (!selectedGrade) {
				setSelectedGrade(seasonGrades[0]);
			}
		})();
	}, [selectedSeason, selectedGrade, orgId]);

	useEffect(() => {
		(async () => {
			if (selectedGrade) {
				const scores = await getScorecards(selectedGrade.Id, orgId);
				const battingRoundScores: Record<string, string | number | null>[] = [];
				const bowlingRoundScores: Record<string, string | number | null>[] = [];
				scores.forEach(score => {
					const ourBattingInnings = score.Innings.filter(inn =>
						teams.find(team => team.Name === inn.Name.split("-")[1].trim())
					);
					const ourBowlingInnings = score.Innings.filter(
						inn => !teams.find(team => team.Name === inn.Name.split("-")[1].trim())
					);

					const opponent = score.Teams.find(team => team.OwningOrganisation.Id !== orgId);

					if (ourBattingInnings.length === 1) {
						const battingRoundObj: Record<string, string | number | null> = {
							name:
								score.Round.ShortName +
								"-" +
								opponent?.OwningOrganisation.ShortName.replace("CC", "")
						};

						if (ourBattingInnings[0]) {
							ourBattingInnings[0].Batting.forEach(batter => {
								battingRoundObj[batter.PlayerShortName] = batter.RunsScored;
							});
							battingRoundScores.push(battingRoundObj);
						}
					} else {
						const battingRoundObj1: Record<string, string | number | null> = {
							name:
								score.Round.ShortName +
								"-" +
								opponent?.OwningOrganisation.ShortName.replace("CC", "") +
								"-1"
						};
						if (ourBattingInnings[0]) {
							ourBattingInnings[0].Batting.forEach(batter => {
								battingRoundObj1[batter.PlayerShortName] = batter.RunsScored;
							});
							battingRoundScores.push(battingRoundObj1);
						}
						const battingRoundObj2: Record<string, string | number | null> = {
							name:
								score.Round.ShortName +
								"-" +
								opponent?.OwningOrganisation.ShortName.replace("CC", "") +
								"-2"
						};
						if (ourBattingInnings[1]) {
							ourBattingInnings[1].Batting.forEach(batter => {
								battingRoundObj2[batter.PlayerShortName] = batter.RunsScored;
							});
							battingRoundScores.push(battingRoundObj2);
						}
					}

					if (ourBowlingInnings.length === 1) {
						const bowlingRoundObj: Record<string, string | number | null> = {
							name:
								score.Round.ShortName +
								"-" +
								opponent?.OwningOrganisation.ShortName.replace("CC", "")
						};

						if (ourBowlingInnings[0]) {
							ourBowlingInnings[0].Bowling.forEach(bowler => {
								bowlingRoundObj[bowler.PlayerShortName] = bowler.WicketsTaken;
							});
							bowlingRoundScores.push(bowlingRoundObj);
						}
					} else {
						const bowlingRoundObj1: Record<string, string | number | null> = {
							name:
								score.Round.ShortName +
								"-" +
								opponent?.OwningOrganisation.ShortName.replace("CC", "") +
								"-1"
						};
						if (ourBowlingInnings[0]) {
							ourBowlingInnings[0].Bowling.forEach(bowler => {
								bowlingRoundObj1[bowler.PlayerShortName] = bowler.WicketsTaken;
							});
							bowlingRoundScores.push(bowlingRoundObj1);
						}
						const bowlingRoundObj2: Record<string, string | number | null> = {
							name:
								score.Round.ShortName +
								"-" +
								opponent?.OwningOrganisation.ShortName.replace("CC", "") +
								"-2"
						};
						if (ourBowlingInnings[1]) {
							ourBowlingInnings[1].Bowling.forEach(bowler => {
								bowlingRoundObj2[bowler.PlayerShortName] = bowler.WicketsTaken;
							});
							bowlingRoundScores.push(bowlingRoundObj2);
						}
					}
				});

				const batters = uniq(
					battingRoundScores
						.map(round => Object.keys(round).filter(name => name !== "name"))
						.reduce((acc, name) => [...acc, ...name], [])
				);

				const bowlers = uniq(
					bowlingRoundScores
						.map(round => Object.keys(round).filter(name => name !== "name"))
						.reduce((acc, name) => [...acc, ...name], [])
				);
				setBattersList(batters);
				setBowlersList(bowlers);
				setBattingChartData([{ "": null }, ...battingRoundScores, { "": null }]);
				setBowlingChartData([{ "": null }, ...bowlingRoundScores, { "": null }]);

				setSelectedBatters(prev => {
					const validSelection = prev.filter(name => batters.includes(name));
					if (validSelection.length || !batters.length) {
						return validSelection;
					}

					return getRandomSelection(batters, 3);
				});

				setSelectedBowlers(prev => {
					const validSelection = prev.filter(name => bowlers.includes(name));
					if (validSelection.length || !bowlers.length) {
						return validSelection;
					}

					return getRandomSelection(bowlers, 3);
				});
			}
		})();
	}, [selectedGrade, orgId, teams, selectedSeason]);

	return (
		<div className="mx-auto max-sm:w-full w-5/6 p-5 md:p-6">
			<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
				<Select
					label="Season"
					variant="filled"
					data={seasons.map(s => s.Name)}
					value={selectedSeason?.Name}
					onChange={season => setSelectedSeason(seasons.find(s => s.Name === season) || null)}
					allowDeselect={false}
					classNames={{
						input: "bg-slate-900/70 border-slate-600 text-slate-100",
						label: "text-slate-300",
						dropdown: "bg-white border border-slate-300 text-slate-900",
						option:
							"text-slate-900 data-[hovered]:bg-slate-100 data-[checked]:bg-emerald-100 data-[checked]:text-slate-900"
					}}
				/>
				<Select
					label="Grade"
					variant="filled"
					data={grades.map(g => g.Name)}
					value={selectedGrade?.Name}
					onChange={g => setSelectedGrade(grades.find(grade => grade.Name === g) || null)}
					allowDeselect={false}
					classNames={{
						input: "bg-slate-900/70 border-slate-600 text-slate-100",
						label: "text-slate-300",
						dropdown: "bg-white border border-slate-300 text-slate-900",
						option:
							"text-slate-900 data-[hovered]:bg-slate-100 data-[checked]:bg-emerald-100 data-[checked]:text-slate-900"
					}}
				/>

				<MultiSelect
					color="blue"
					variant="filled"
					label="Batters"
					searchable
					data={battersList}
					value={selectedBatters}
					onChange={selected => {
						setSelectedBatters(selected);
					}}
					placeholder="Search batters"
					classNames={{
						input: "bg-slate-900/70 border-slate-600 text-slate-100",
						label: "text-slate-300",
						dropdown: "bg-white border border-slate-300 text-slate-900",
						option:
							"text-slate-900 data-[hovered]:bg-slate-100 data-[checked]:bg-emerald-100 data-[checked]:text-slate-900"
					}}
				/>
			</div>

			<div className="h-[360px] md:h-[430px] mt-5">
				<ResponsiveContainer width="100%" height="100%">
					<ComposedChart
						className="mt-2"
						data={battingChartData}
						margin={{
							top: 12,
							right: 24,
							left: 8,
							bottom: 8
						}}
					>
						<XAxis dataKey="name" stroke="#94a3b8" />
						<YAxis
							label={{ value: "Runs", angle: -90, position: "insideLeft", fill: "#94a3b8" }}
							stroke="#94a3b8"
						/>
						<Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155" }} />
						<Legend />

						{selectedBatters.map(name => {
							const color = getColorForName(name);
							return (
								<Line
									fill={color}
									key={name}
									type="monotone"
									dataKey={name}
									stroke={color}
									strokeWidth={3}
									activeDot={{ r: 7 }}
									connectNulls
								/>
							);
						})}
					</ComposedChart>
				</ResponsiveContainer>
			</div>

			<div className="grid grid-cols-1 mt-6">
				<MultiSelect
					color="blue"
					variant="filled"
					label="Bowlers"
					searchable
					data={bowlersList}
					value={selectedBowlers}
					onChange={selected => {
						setSelectedBowlers(selected);
					}}
					placeholder="Search bowlers"
					classNames={{
						input: "bg-slate-900/70 border-slate-600 text-slate-100",
						label: "text-slate-300",
						dropdown: "bg-white border border-slate-300 text-slate-900",
						option:
							"text-slate-900 data-[hovered]:bg-slate-100 data-[checked]:bg-emerald-100 data-[checked]:text-slate-900"
					}}
				/>
			</div>

			<div className="h-[360px] md:h-[430px] mt-5">
				<ResponsiveContainer width="100%" height="100%">
					<ComposedChart
						className="mt-2"
						data={bowlingChartData}
						margin={{
							top: 12,
							right: 24,
							left: 8,
							bottom: 8
						}}
					>
						<XAxis dataKey="name" stroke="#94a3b8" />
						<YAxis
							label={{ value: "Wickets", angle: -90, position: "insideLeft", fill: "#94a3b8" }}
							stroke="#94a3b8"
						/>
						<Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155" }} />
						<Legend />

						{selectedBowlers.map(name => {
							const color = getColorForName(name);
							return (
								<Line
									fill={color}
									key={name}
									type="monotone"
									dataKey={name}
									stroke={color}
									strokeWidth={3}
									activeDot={{ r: 7 }}
									connectNulls
								/>
							);
						})}
					</ComposedChart>
				</ResponsiveContainer>
			</div>
		</div>
	);
};

export default SeasonInsights;
